import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import {
  MatMenu,
  MatMenuItem,
  MatMenuModule,
  MatMenuTrigger
} from '@angular/material/menu';
import { MatToolbar } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import introJs from 'intro.js';

import { AccountSignatureComponent } from '@accounts/components/account-signature/account-signature.component';
import { Account } from '@accounts/models/account';
import { AccountType } from '@accounts/models/account-type';
import { AuthService } from '@auth/services/auth.service';
import { APP_STORE } from '@core/stores/app.store';
import { USER_STORE } from '@core/stores/user.store';
import { ENVIRONMENT } from '@env/environment';
import { Module } from '@modules/models/module';
import { ModuleHasClientAccessPipe } from '@modules/pipes/module-has-client-access.pipe';
import { MODULES_STORE } from '@modules/stores/modules.store';
import { TermsAndConditionsComponent } from '@shared/components/terms-and-conditions/terms-and-conditions.component';
import { SortPipe } from '@shared/pipes/sort.pipe';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    RouterLink,

    MatMenuModule,
    MatToolbar,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatButton,
    MatIcon,

    AccountSignatureComponent,
    SortPipe
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavbarComponent {
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialog = inject(MatDialog);
  readonly #authService = inject(AuthService);
  readonly #moduleHasClientAccessPipe = inject(ModuleHasClientAccessPipe);

  readonly #appStore = inject(APP_STORE);
  readonly #userStore = inject(USER_STORE);
  readonly #modulesStore = inject(MODULES_STORE);

  readonly user = this.#userStore.data;
  readonly selectedAccount = this.#appStore.selectedAccount;
  readonly modules = this.#modulesStore.data;

  readonly isSystemAdmin = computed(
    () => this.selectedAccount()?.accountType === AccountType.SystemAdmin
  );

  readonly sidenavPinned = this.#appStore.sidenavPinned;

  readonly modulesWithAccess = computed(() => {
    const account = this.#appStore.selectedAccount();
    const modules = this.#modulesStore.data();
    if (!modules?.length || !account) {
      return [];
    }
    return modules.filter(mod =>
      this.#moduleHasClientAccessPipe.transform(mod, account)
    );
  });

  readonly modulesWithoutAccess = computed(() => {
    const account = this.#appStore.selectedAccount();
    const modules = this.#modulesStore.data();
    if (!modules?.length || !account) {
      return [];
    }
    return modules.filter(
      mod => !this.#moduleHasClientAccessPipe.transform(mod, account)
    );
  });

  readonly CORE_MODULE = ENVIRONMENT.coreModule as Module;

  termsHandled = false;
  guidanceHandled = false;

  constructor() {
    effect(() => {
      const u = this.#userStore.data();
      const acc = this.selectedAccount();

      if (!u || !acc) {
        return;
      }
      if (u.termsAndConditionsAccepted) {
        return;
      }
      if (this.termsHandled) {
        return;
      }

      this.termsHandled = true;

      const dialogRef = this.#dialog.open(TermsAndConditionsComponent, {
        autoFocus: false,
        disableClose: true
      });

      this.#destroyRef.onDestroy(() => {
        dialogRef.close();
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(result => {
          if (!result) {
            this.signOut();
            return;
          }

          this.#userStore.confirmTermsAndConditions();
        });
    });

    effect(() => {
      const u = this.user();
      const acc = this.selectedAccount();

      if (!u || !acc) {
        return;
      }
      if (!u.termsAndConditionsAccepted) {
        return;
      }
      if (u.guidanceDisplayed) {
        return;
      }
      if (this.guidanceHandled) {
        return;
      }

      this.guidanceHandled = true;

      this.startIntroJs();
    });
  }

  selectAccount(account: Account): void {
    this.#appStore.selectAccount(account);

    switch (account.accountType) {
      case AccountType.ClientAdmin:
        void this.#router.navigate([`/clients/${account.client?.id}/details`]);
        break;

      case AccountType.ClientUser:
        void this.#router.navigate(['/my-profile']);
        break;
    }
  }

  navigateToModule(module: Module): void {
    const clientId = this.#appStore.selectedClientId();
    const query = clientId ? `?clientId=${clientId}` : '';
    let url = `${module.locationUrl}${query}`;

    const account = this.selectedAccount();
    if (
      account &&
      module.name !== ENVIRONMENT.coreModule.name &&
      !this.#moduleHasClientAccessPipe.transform(module, account)
    ) {
      url = module.landingPageUrl ?? ENVIRONMENT.appRoot;
    }

    if (url !== ENVIRONMENT.appRoot) {
      window.open(url, '_blank');
      return;
    }

    void this.#router.navigateByUrl('/');
  }

  signOut(): void {
    this.#authService.signOut();
  }

  // #region Private methods

  private startIntroJs(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void introJs()
          .setOptions({
            tooltipClass: 'custom-intro-tooltip',
            showButtons: false,
            showBullets: false,
            scrollToElement: false,
            helperElementPadding: 0,
            steps: [
              {
                element: document.querySelector('.modulePicker')!,
                title: 'Welcome!',
                intro: 'Use the module picker to switch between apps',
                position: 'bottom'
              }
            ]
          })
          .onExit(() => {
            this.#userStore.updateGuidanceDisplayed();
          })
          .start();
      });
    });
  }
}
